import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";

/**
 * Shared shell for legal pages (ToS, Privacy).
 * Props:
 *   title: string
 *   effectiveDate: string
 *   version: string
 *   sections: [{ id, title, content: ReactNode }]
 */
export default function LegalPage({ title, effectiveDate, version, sections }) {
    const [active, setActive] = useState(sections[0]?.id);

    useEffect(() => {
        const onScroll = () => {
            const scrollY = window.scrollY + 140;
            let current = sections[0]?.id;
            for (const s of sections) {
                const el = document.getElementById(s.id);
                if (el && el.offsetTop <= scrollY) current = s.id;
            }
            setActive(current);
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        onScroll();
        return () => window.removeEventListener("scroll", onScroll);
    }, [sections]);

    return (
        <div className="min-h-screen bg-ocean-mesh">
            <header className="sticky top-0 z-30 glass-panel">
                <div className="max-w-6xl mx-auto h-16 px-6 flex items-center justify-between">
                    <Link to="/"><Logo /></Link>
                    <nav className="flex items-center gap-6 text-sm text-muted-foreground">
                        <Link to="/terms" className="hover:text-ocean-700 transition-colors">Terms</Link>
                        <Link to="/privacy" className="hover:text-ocean-700 transition-colors">Privacy</Link>
                        <Link to="/pricing" className="hover:text-ocean-700 transition-colors">Pricing</Link>
                        <Link to="/login" className="hover:text-ocean-700 transition-colors">Sign in</Link>
                    </nav>
                </div>
            </header>

            <div className="max-w-6xl mx-auto px-6 py-12 lg:py-16">
                <div className="mb-10">
                    <div className="text-xs uppercase tracking-[0.2em] text-secondary-dark font-semibold">Legal</div>
                    <h1 className="mt-2 font-heading text-4xl sm:text-5xl text-ocean-700 tracking-tight">{title}</h1>
                    <p className="mt-3 text-sm text-muted-foreground">
                        Effective date: <span className="text-ocean-700 font-medium">{effectiveDate}</span> · Version {version}
                    </p>
                </div>

                <div className="grid lg:grid-cols-[260px,1fr] gap-10">
                    <aside className="hidden lg:block">
                        <div className="sticky top-24">
                            <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-3">On this page</div>
                            <ul className="space-y-1 text-sm">
                                {sections.map((s, i) => (
                                    <li key={s.id}>
                                        <a
                                            href={`#${s.id}`}
                                            className={`block px-3 py-1.5 rounded-lg border-l-2 transition-colors ${
                                                active === s.id
                                                    ? "border-secondary text-ocean-700 bg-ocean-50 font-medium"
                                                    : "border-transparent text-muted-foreground hover:text-ocean-700 hover:bg-sand-100"
                                            }`}
                                        >
                                            {i + 1}. {s.title}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </aside>

                    <article className="rounded-3xl bg-white border border-sand-200 shadow-card p-8 sm:p-12 prose-legal">
                        {sections.map((s, i) => (
                            <section key={s.id} id={s.id} className="scroll-mt-24 mb-10 last:mb-0">
                                <h2 className="font-heading text-2xl text-ocean-700 tracking-tight">
                                    {i + 1}. {s.title}
                                </h2>
                                <div className="mt-4 space-y-4 text-[15px] text-ocean-700/90 leading-relaxed">
                                    {s.content}
                                </div>
                            </section>
                        ))}

                        <div className="mt-12 pt-6 border-t border-sand-200 text-xs text-muted-foreground">
                            <p>
                                If you have questions about this document, contact <a href="mailto:legal@blueledger.io" className="text-secondary-dark hover:underline">legal@blueledger.io</a> or write to:
                            </p>
                            <p className="mt-2 not-italic">
                                Modern Business Solutions Ltd — Legal Department<br />
                                PO Box 1234, Grand Cayman, KY1-1103<br />
                                Cayman Islands
                            </p>
                        </div>
                    </article>
                </div>

                <div className="mt-16 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-muted-foreground">
                    <Logo />
                    <div>© {new Date().getFullYear()} Modern Business Solutions Ltd. All rights reserved.</div>
                    <div className="flex gap-5">
                        <Link to="/terms">Terms</Link>
                        <Link to="/privacy">Privacy</Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
